import { useEffect, useRef, useState } from 'react'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  STANDARD_SOURCE_LANGUAGE,
  isSupportedLanguage,
  type SupportedLanguage,
} from '@/shared/config/languages'
import { useListAssociatedPartiesForProduct } from '@/shared/hooks/api/associatedParties'
import {
  useGetInstructionHandbookDocument,
  useGetMachineryDirectiveCompliance,
  useUpdateInstructionHandbookDocument,
  useCreateInstructionHandbookDocumentVersion,
  useStartInstructionHandbookGeneration,
  useExportInstructionHandbookPdf,
  type InstructionHandbookSectionRecord,
  type InstructionHandbookStreamEvent,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useGetProduct } from '@/shared/hooks/api/product'
import { buildInstructionHandbookPdfFilename } from '@/shared/utils/pdf-export-filenames'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog } from '@/components/confirm-dialog'
import {
  ComplianceDocumentStreamingPanel,
  COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS,
} from '../../../shared/compliance-document/ComplianceDocumentStreamingPanel'
import { ComplianceDocumentGenerateMenu } from '../../components/ComplianceDocumentGenerateMenu'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { RewriteInstructionRefusedDialog } from '../../components/RewriteInstructionRefusedDialog'
import { RewriteInsufficientSourcesDialog } from '../../components/RewriteInsufficientSourcesDialog'
import { SectionRegenerateConfirmDialog } from '../../components/SectionRegenerateConfirmDialog'
import { ComplianceDocumentManifestProvider } from '../../context/ComplianceDocumentManifestContext'
import { useComplianceDocumentRewrite } from '../../hooks/useComplianceDocumentRewrite'
import type { ComplianceDocumentViewHandle } from '../../types/complianceDocumentViewHandle'
import {
  buildCompliancePdfCoverMarkdown,
  formatCompliancePdfDate,
} from '../../utils/buildCompliancePdfCoverMarkdown'
import { parseComplianceDocumentSavedAt } from '../../utils/formatComplianceDocumentLastSavedAt'
import { isComplianceDocumentStaleAfterClassificationChange } from '../../utils/isComplianceDocumentStaleAfterClassificationChange'
import { resolveComplianceDocumentSectionsToGenerate } from '../../utils/resolveComplianceDocumentSectionsToGenerate'
import { RewriteDrawer } from '../risk-assessment/RewriteDrawer'
import { InstructionHandbookDocumentView } from './InstructionHandbookDocumentView'

export function ManualGenerationPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId ?? ''
  const productId = params.productId ?? ''
  const { t, i18n } = useTranslation([
    'machineryDirectiveCompliance',
    'associatedParties',
    'countries',
  ])

  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine === true
  const pageTitleKey = isPartlyCompletedMachine
    ? 'assemblyInstructions.title'
    : 'manualGenerationPage.title'

  const { data: document, isLoading } =
    useGetInstructionHandbookDocument(complianceId)
  const { data: product } = useGetProduct(productId)
  const { data: associatedPartyAssignments = [] } =
    useListAssociatedPartiesForProduct(productId)
  const updateDocument = useUpdateInstructionHandbookDocument(complianceId)
  const createDocumentVersion =
    useCreateInstructionHandbookDocumentVersion(complianceId)
  const startGeneration = useStartInstructionHandbookGeneration(complianceId)
  const exportPdf = useExportInstructionHandbookPdf(complianceId)

  const [isGenerating, setIsGenerating] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [streamingSectionId, setStreamingSectionId] = useState<string | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [sections, setSections] = useState<InstructionHandbookSectionRecord[]>(
    []
  )
  const [contentSyncKey, setContentSyncKey] = useState(0)

  const [pendingFullRegenerate, setPendingFullRegenerate] = useState<{
    language: SupportedLanguage
  } | null>(null)
  const [pendingRegenerateSectionId, setPendingRegenerateSectionId] = useState<
    string | null
  >(null)
  const [generationLanguage, setGenerationLanguage] =
    useState<SupportedLanguage>(STANDARD_SOURCE_LANGUAGE)
  const documentViewRef = useRef<ComplianceDocumentViewHandle>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  const displaySectionsForRewrite =
    sections.length > 0 ? sections : (document?.sections ?? [])

  const rewrite = useComplianceDocumentRewrite({
    complianceId,
    documentType: 'INSTRUCTION_HANDBOOK',
    sections: displaySectionsForRewrite,
    translate: (key) => t(`manualGenerationPage.${key}`),
    getFlushedSectionMarkdown: (sectionId) => {
      const flushedSections =
        documentViewRef.current?.flushSectionsToState() ??
        displaySectionsForRewrite
      return (
        flushedSections.find((entry) => entry.sectionId === sectionId)
          ?.markdown ?? null
      )
    },
    onOpenRewritePanel: () => {
      documentViewRef.current?.cancelAutosave()
    },
  })

  useEffect(() => {
    if (document == null) {
      if (
        compliance?.contentLanguage != null &&
        isSupportedLanguage(compliance.contentLanguage)
      ) {
        setGenerationLanguage(compliance.contentLanguage)
      }
      return
    }
    setSections(document.sections ?? [])
    setContentSyncKey((previous) => previous + 1)
    setLastSavedAt(
      parseComplianceDocumentSavedAt(document.generatedAt, document.createdAt)
    )
    if (document.language != null && isSupportedLanguage(document.language)) {
      setGenerationLanguage(document.language)
    }
  }, [
    document?.id,
    document?.versionNumber,
    document?.language,
    compliance?.contentLanguage,
  ])

  const emptyStateStreamingStatusLabel =
    streamingSectionId != null
      ? t('manualGenerationPage.generatingSection', {
          section: streamingSectionId,
        })
      : t('manualGenerationPage.generating')

  const runGeneration = (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
    sectionIds?: string[]
  }) => {
    setError(null)
    setStreamingContent('')
    setStreamingSectionId(null)
    setIsGenerating(true)

    const sectionsToGenerate = resolveComplianceDocumentSectionsToGenerate({
      fullDocument: payload.fullDocument,
      sectionIds: payload.sectionIds,
    })

    startGeneration.mutate(
      {
        sectionsToGenerate,
        language:
          payload.fullDocument === true
            ? (payload.language ?? generationLanguage)
            : undefined,
        onEvent: (event: InstructionHandbookStreamEvent) => {
          switch (event.type) {
            case 'document_section.started':
              setStreamingSectionId(event.section)
              setStreamingContent('')
              break
            case 'document_section.progress':
              if (event.text) {
                setStreamingContent((prev) => prev + event.text)
              }
              break
            case 'document_section.completed':
              break
            case 'document.completed':
              if (event.sections != null && event.sections.length > 0) {
                setSections(event.sections)
                setContentSyncKey((previous) => previous + 1)
              }
              break
            case 'error':
              setError(event.error)
              break
            default:
              break
          }
        },
        onComplete: () => {
          setIsGenerating(false)
          toast.success(t('manualGenerationPage.successToast'))
          window.setTimeout(() => {
            setStreamingContent('')
            setStreamingSectionId(null)
          }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
        },
      },
      {
        onError: (err) => {
          setIsGenerating(false)
          setError(err.message)
          toast.error(t('manualGenerationPage.generateFailed'))
          window.setTimeout(() => {
            setStreamingContent('')
            setStreamingSectionId(null)
          }, COMPLIANCE_DOCUMENT_STREAMING_PANEL_TRANSITION_MS)
        },
      }
    )
  }

  const startGenerationWithSave = async (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
    sectionIds?: string[]
  }) => {
    try {
      await documentViewRef.current?.flushAndAutosaveSilent()
    } catch {
      return
    }
    runGeneration(payload)
  }

  const handleGenerate = (payload: {
    language?: SupportedLanguage
    fullDocument?: boolean
  }) => {
    const isFullDocumentRegenerate = payload.fullDocument === true
    const hasExistingDocument =
      sections.length > 0 || (document?.sections?.length ?? 0) > 0

    if (isFullDocumentRegenerate && hasExistingDocument) {
      setPendingFullRegenerate({
        language: payload.language ?? generationLanguage,
      })
      return
    }

    void startGenerationWithSave(payload)
  }

  const handleRegenerateSection = (sectionId: string) => {
    setPendingRegenerateSectionId(sectionId)
  }

  const handleConfirmSectionRegenerate = (sectionId: string) => {
    setPendingRegenerateSectionId(null)
    void startGenerationWithSave({
      fullDocument: false,
      sectionIds: [sectionId],
    })
  }

  const handleConfirmFullRegenerate = () => {
    if (pendingFullRegenerate == null) {
      return
    }
    const language = pendingFullRegenerate.language
    setPendingFullRegenerate(null)
    void startGenerationWithSave({ fullDocument: true, language })
  }

  const handleExportPdf = async () => {
    if (!document) {
      toast.error(t('manualGenerationPage.exportFailed'))
      return
    }
    const manufacturerAssignment = associatedPartyAssignments.find(
      (assignment) => assignment.role === 'MANUFACTURER'
    )
    const manufacturerParty = manufacturerAssignment?.associatedParty ?? null
    const productName = product?.name ?? ''
    const model = product?.productModel ?? ''
    const yearRaw = product?.yearOfManufacture
    const year = yearRaw == null ? '' : String(yearRaw)
    const timestampIso =
      document.generatedAt ?? document.createdAt ?? new Date().toISOString()

    const pdfManualTitle = t('pdfExportCover.userManualTitle')
    const coverMarkdown = buildCompliancePdfCoverMarkdown({
      documentTitle: pdfManualTitle,
      labels: {
        manufacturer: t('pdfExportCover.manufacturer'),
        productName: t('pdfExportCover.productName'),
        model: t('pdfExportCover.model'),
        year: t('pdfExportCover.year'),
        documentCreated: t('pdfExportCover.documentCreated'),
        notAvailable: t('pdfExportCover.notAvailable'),
      },
      manufacturerParty,
      getCountryName: (code) => t(code, { ns: 'countries' }) || code,
      translateAssociatedParty: (key, options) =>
        t(key, { ns: 'associatedParties', ...options }),
      productName,
      model,
      year,
      documentTimestampIso: timestampIso,
      formatDate: (iso) => formatCompliancePdfDate(iso, i18n.language),
    })

    try {
      const exportFilename = buildInstructionHandbookPdfFilename(new Date())
      await exportPdf.mutateAsync({
        coverMarkdown,
        documentName: exportFilename,
      })
      toast.success(t('manualGenerationPage.pdfExportedSuccess'))
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('manualGenerationPage.exportFailed')
      )
    }
  }

  const handleAutosave = async (
    sections: InstructionHandbookSectionRecord[]
  ) => {
    try {
      await updateDocument.mutateAsync({
        sections,
        skipCacheInvalidation: true,
      })
      setLastSavedAt(new Date())
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('manualGenerationPage.saveFailed')
      )
      throw err
    }
  }

  const handleCreateVersion = async (
    sections: InstructionHandbookSectionRecord[]
  ) => {
    try {
      const created = await createDocumentVersion.mutateAsync({ sections })
      setSections(created.sections ?? [])
      setContentSyncKey((previous) => previous + 1)
      setLastSavedAt(
        parseComplianceDocumentSavedAt(
          created.generatedAt,
          created.createdAt
        ) ?? new Date()
      )
      toast.success(
        t('manualGenerationPage.saveVersionSuccess', {
          versionNumber: created.versionNumber,
        })
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('manualGenerationPage.saveFailed')
      )
      throw err
    }
  }

  const openRewritePanel = rewrite.openRewritePanel

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  const hasError = error != null
  const hasDocument =
    sections.length > 0 || (document?.sections?.length ?? 0) > 0
  const displaySections =
    sections.length > 0 ? sections : (document?.sections ?? [])
  const showClassificationStaleWarning =
    hasDocument &&
    isComplianceDocumentStaleAfterClassificationChange(
      compliance?.isPartlyCompletedMachineUpdatedAt,
      document?.generatedAt
    )

  const pendingRegenerateSection = pendingRegenerateSectionId
    ? displaySections.find(
        (section) => section.sectionId === pendingRegenerateSectionId
      )
    : undefined

  const fullRegenerateConfirmDialog = (
    <ConfirmDialog
      open={pendingFullRegenerate != null}
      onOpenChange={(open) => {
        if (!open) {
          setPendingFullRegenerate(null)
        }
      }}
      handleConfirm={handleConfirmFullRegenerate}
      title={t('manualGenerationPage.fullRegenerateCustomSectionsDialog.title')}
      desc={t(
        'manualGenerationPage.fullRegenerateCustomSectionsDialog.description'
      )}
      cancelBtnText={t('manualGenerationPage.cancel')}
      confirmText={t(
        'manualGenerationPage.fullRegenerateCustomSectionsDialog.confirm'
      )}
      destructive
      isLoading={isGenerating || startGeneration.isPending}
    />
  )

  const sectionRegenerateConfirmDialog = (
    <SectionRegenerateConfirmDialog
      open={pendingRegenerateSectionId != null}
      onOpenChange={(open) => {
        if (!open) {
          setPendingRegenerateSectionId(null)
        }
      }}
      sectionId={pendingRegenerateSectionId}
      sectionTitle={pendingRegenerateSection?.title ?? null}
      sectionKind={pendingRegenerateSection?.kind}
      documentType='instruction_handbook'
      translationKeyPrefix='manualGenerationPage'
      onConfirm={handleConfirmSectionRegenerate}
      isLoading={isGenerating || startGeneration.isPending}
    />
  )

  const renderGenerateMenu = (options: {
    isRegenerate: boolean
    fullDocumentOnly?: boolean
    size?: 'default' | 'sm'
    className?: string
    withFileIcon?: boolean
  }) => (
    <ComplianceDocumentGenerateMenu
      {...options}
      translationKeyPrefix='manualGenerationPage'
      generationLanguage={generationLanguage}
      onGenerationLanguageChange={setGenerationLanguage}
      onGenerate={handleGenerate}
      isGeneratePending={
        startGeneration.isPending ||
        updateDocument.isPending ||
        createDocumentVersion.isPending
      }
    />
  )

  const pageHeader = (
    <ComplianceSubPageHeader>
      <ComplianceSubPageTitle>{t(pageTitleKey)}</ComplianceSubPageTitle>
      {hasError && (
        <Alert variant='destructive'>
          <AlertTitle>{error}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </ComplianceSubPageHeader>
  )

  const rewriteBubbleMenu = (
    <Button type='button' variant='ghost' size='sm' onClick={openRewritePanel}>
      {t('manualGenerationPage.rewrite')}
    </Button>
  )

  const pageContent = hasDocument ? (
    <>
      <div className='flex h-[calc(100dvh-11rem)] min-h-80 flex-col space-y-4 overflow-hidden'>
        {pageHeader}
        {showClassificationStaleWarning && (
          <Alert variant='warning'>
            <AlertTitle>
              {t('classificationChangeStaleDocument.title')}
            </AlertTitle>
            <AlertDescription>
              {t('classificationChangeStaleDocument.description')}
            </AlertDescription>
          </Alert>
        )}
        <InstructionHandbookDocumentView
          ref={documentViewRef}
          complianceId={complianceId}
          sections={displaySections}
          onSectionsChange={setSections}
          onAutosave={handleAutosave}
          onCreateVersion={handleCreateVersion}
          isAutosaving={updateDocument.isPending}
          isCreatingVersion={createDocumentVersion.isPending}
          lastSavedAt={lastSavedAt}
          isGenerating={isGenerating}
          onExportPdf={handleExportPdf}
          isExportPending={exportPdf.isPending}
          generateMenu={renderGenerateMenu({
            isRegenerate: true,
            size: 'sm',
          })}
          onRegenerateSection={handleRegenerateSection}
          contentSyncKey={contentSyncKey}
          streamingSectionId={streamingSectionId}
          streamingContent={streamingContent}
          onFocusedEditorChange={rewrite.handleFocusedEditorChange}
          bubbleMenuContent={rewriteBubbleMenu}
          bubbleMenuHidden={rewrite.isRewritePanelOpen}
          rewritePanelOpen={rewrite.isRewritePanelOpen}
        />
      </div>
      <RewriteDrawer
        isOpen={rewrite.isRewritePanelOpen}
        onClose={rewrite.closeRewritePanel}
        selectedText={rewrite.selectedTextInPanel}
        instruction={rewrite.rewriteInstructionValue}
        onInstructionChange={rewrite.setRewriteInstructionValue}
        onRewrite={async () => {
          try {
            await rewrite.handleRewrite()
          } catch {
            toast.error(t('manualGenerationPage.rewriteFailed'))
          }
        }}
        isRewritePending={rewrite.isRewritePending}
        result={rewrite.rewriteResult}
        hasResult={(rewrite.rewriteResult?.length ?? 0) > 0}
        onAccept={rewrite.handleAcceptRewrite}
        onReject={rewrite.handleRejectRewrite}
        blockedMessage={rewrite.rewriteBlockedMessage}
      />
      <RewriteInsufficientSourcesDialog
        open={rewrite.isInsufficientSourcesDialogOpen}
        onOpenChange={rewrite.setIsInsufficientSourcesDialogOpen}
        translationKeyPrefix='manualGenerationPage'
      />
      <RewriteInstructionRefusedDialog
        open={rewrite.isInstructionRefusedDialogOpen}
        onOpenChange={rewrite.setIsInstructionRefusedDialogOpen}
        translationKeyPrefix='manualGenerationPage'
      />
      {fullRegenerateConfirmDialog}
      {sectionRegenerateConfirmDialog}
    </>
  ) : (
    <>
      <div className='flex h-full flex-col space-y-4'>
        {pageHeader}

        <ComplianceDocumentStreamingPanel
          isOpen={isGenerating}
          statusLabel={emptyStateStreamingStatusLabel}
          streamingContent={streamingContent}
        />

        {!isGenerating && !error && (
          <Empty className='min-h-[320px] rounded-lg border border-dashed'>
            <EmptyHeader>
              <EmptyMedia variant='icon'>
                <FileText className='size-10' />
              </EmptyMedia>
              <EmptyTitle>{t(pageTitleKey)}</EmptyTitle>
              <EmptyDescription>
                {t('manualGenerationPage.emptyStateMessage')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              {renderGenerateMenu({
                isRegenerate: false,
                fullDocumentOnly: true,
                size: 'sm',
                className: 'gap-2',
                withFileIcon: true,
              })}
            </EmptyContent>
          </Empty>
        )}
      </div>
      {fullRegenerateConfirmDialog}
      {sectionRegenerateConfirmDialog}
    </>
  )

  const manifestLocale =
    document?.language != null && isSupportedLanguage(document.language)
      ? document.language
      : generationLanguage

  return (
    <ComplianceDocumentManifestProvider
      documentKind='instruction_handbook'
      locale={manifestLocale}
    >
      {pageContent}
    </ComplianceDocumentManifestProvider>
  )
}
